<template>
    <div class="w-full absolute top-0">
        <div class="relative flex flex-col gap-16 items-center justify-center pt-34 mb-28">
            <div class="relative container grid grid-cols-3 md:grid-cols-12 gap-y-16 md:gap-x-0">
                <div class="col-span-full md:col-span-6 lg:!col-start-1 grid grid-cols-3 md:grid-cols-6 gap-y-16">
                    <div class="view-h1 col-span-full lg:!col-start-1">{{get_localized_value(data.title)}}</div>
                    <div class="col-span-full md:col-span-3 flex flex-col">
                        <div class="flex flex-col gap-8">
                            <h3 class="view-h3 !first:col-start-1">{{get_localized_value(data['contact-us'].title)}}</h3>
                            <div class="flex flex-col gap-0">
                                <div>{{data['contact-us']['telephone-number']}}</div>
                                <div>{{data['contact-us']['e-mail']}}</div>
                            </div>
                            <div class="flex flex-col gap-0">
                                <div>{{t('vat')}}: NL854720005B01</div>
                                <div>{{t('kvk')}}: 62236083</div>
                                <div>{{t('iban')}}: NL62INGB0006732644</div>
                            </div>
                            <div class="flex flex-row gap-2">
                                <div class="w-12 h-12">
                                    <a href="https://nl.linkedin.com/company/yipp-interactive" target="_blank" class="w-12 h-12"><LinkedInIcon class="w-full h-full"/></a>
                                </div>
                                <div class="w-12 h-12">
                                    <a href="https://www.instagram.com/yippinteractive/" target="_blank" class="w-12 h-12"><InstagramIcon class="w-full h-full"/></a>
                                </div>
                                <div class="w-12 h-12">
                                    <a href="https://www.youtube.com/@yippinteractive" target="_blank" class="w-12 h-12"><YoutubeIcon class="w-full h-full"/></a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-span-full md:col-span-3 flex flex-col">
                        <div class="flex flex-col gap-8">
                            <h3 class="view-h3">{{get_localized_value(data['visit-us'].title)}}</h3>
                            <div class="flex flex-col gap-0">
                                <div>{{data['visit-us']['street']}}</div>
                                <div>{{data['visit-us']['postal']}}</div>
                                <div>{{get_localized_value(data['visit-us']['country'])}}</div>
                            </div>
                            <div class="flex flex-col gap-0">
                                <a class="underline" :href="data['visit-us'].directions.url">{{get_localized_value(data['visit-us'].directions.label)}}</a>
                            </div>
                        </div>
                    </div>
                    <div class="col-span-full flex flex-col gap-6 md:max-w-5/6">
                        <div class="view-h2">{{ t('newsletter.title') }}</div>
                        <div>{{ t('newsletter.subtitle') }}</div>
                        <newsletter-signup />
                    </div>
                </div>
                <div class="col-span-full md:col-span-6 relative aspect-square md:aspect-auto sm:flex sm:items-center w-full">
                    <ResponsiveImage v-if="data.image" :image-id="data.image" class="md:absolute h-auto top-0 right-0 w-full aspect-[5/6] md:h-[calc(100%+10px)] object-cover object-center sm:left-auto sm:top-auto sm:right-auto"/>
                </div>
            </div>

<!--            <div class="flex flex-col md:flex-row gap-16 container px-8">-->
<!--                <div class="view-h1">{{data.title[mainStore.active_locale]}}</div>-->
<!--            </div>-->

            <ContactBanner :profile-picture="data?.['contact-banner'].image" :left-side="data?.['contact-banner']['left-side']" :right-side="data?.['contact-banner']['right-side']"/>
        </div>
        <Footer />
    </div>
</template>

<script lang="ts" setup>
import Header from "~/components/view/Header.vue"
import { Pages } from "#shared/types/Pages"
import RenderedHTML from "~/components/view/RenderedHTML.vue"
import NewsletterSignup from "~/components/view/NewsletterSignup.vue"
import ContactBanner from "~/components/view/ContactBanner.vue"
import LinkedInIcon from "~/components/icons/LinkedInIcon.vue"
import YoutubeIcon from "~/components/icons/YoutubeIcon.vue"
import InstagramIcon from "~/components/icons/InstagramIcon.vue"
import Footer from "~/components/view/Footer.vue"
import SetImg from "~/components/view/SetImg.vue"

const { t, locale } = useI18n({useScope: 'local'})
const localePath = useLocalePath()
const config = useRuntimeConfig()

const mainStore = useMainStore()
const siteStore = useSiteStore()
const { get_value: get_localized_value } = useLocalizedValue()

const siteUrl = String(config.public.publicHost || 'https://yipp.nl').replace(/\/$/, '')
const canonicalUrl = computed(() => `${siteUrl}${localePath('/contact', locale.value)}`)

useHead(() => ({
    title: t('seo.title')
}))
useSeoMeta({
    description: () => t('seo.description'),
    ogTitle: () => t('seo.title'),
    ogDescription: () => t('seo.description'),
    ogUrl: () => canonicalUrl.value
})

const { data: {value: { data: {data: data}     }} } = await useAsyncData(
    'website-contact',
    () => $fetch<any>('/api/bucketContent/website-contact/latest')
)

definePageMeta({
    layout: 'view',
    pageTransition: {
        onBeforeEnter: (el) => {
            if (window) window.scroll({top: 0})
        }
    }
})

onMounted(() => {
    siteStore.setIconColor('dark')
    siteStore.setCurrentPage(Pages.CONTACT)
})

</script>

<i18n lang="yaml">
en:
    vat: VAT
    kvk: KvK
    iban: IBAN
    newsletter:
        title: Stay up to date
        subtitle: Subscribe to our newsletter
    seo:
        title: "Contact YIPP"
        description: "Get in touch with YIPP in Amsterdam — for project inquiries, collaborations, or to visit our studio."
nl:
    vat: BTW
    kvk: KvK
    iban: IBAN
    newsletter:
        title: Blijf op de hoogte
        subtitle: Schrijf je in op onze nieuwsbrief
    seo:
        title: "Contact YIPP"
        description: "Neem contact op met YIPP in Amsterdam — voor projectaanvragen, samenwerkingen of een bezoek aan onze studio."
</i18n>
